/**
 * Utility functions for manipulating page elements.
 */

import { useDateFormat } from "@vueuse/core";

/**
 * Focuses on the first invalid input element on the page.
 */
export const focusFirstInvalidInput = () => {
    const invalidInput = document.querySelector('.is-invalid') || document.querySelector('.p-invalid');
    if (invalidInput) invalidInput.focus();
};

export const scrollIntoView = (element) => {
    if (!element) return;

    element.scrollIntoView({ behavior: 'smooth' });
};   

/**
 * Returns the head title by appending the provided title with the application title.
 * @param {string} title - The title to be appended.
 * @returns {string} The head title.
 */
export const getHeadTitle = (title) => {
    return title + ' - ' + import.meta.env.VITE_APP_TITLE;
};

export const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

export const formattedDate = (date) => {
    if (!date) return '';
    const formatted = useDateFormat(date, 'MM/DD/YYYY');
    return formatted.value;
};

export const formatNumber = (number, locale = 'en-US') => {
    if (!number) return 0;
    try {
        return number.toLocaleString(locale);
    } catch (e) {
        return number.toString(); // Fallback to default string representation
    }
};

/**
 * Formats a number as currency.
 * @param {number} number - The number to format.
 * @param {string} locale - The locale to use for formatting (e.g., 'en-US').
 * @param {string} currency - The currency to use for formatting (e.g., 'USD').
 * @returns {string} The formatted currency string.
 */
export const formatCurrency = (number, options = {}) => {
    const {locale = 'en-US', currency = 'USD', minimumFractionDigits = 0, maximumFractionDigits = 0 } = options;
    
    if (number === null || number === undefined) {
        return new Intl.NumberFormat(locale, { style: 'currency', currency, minimumFractionDigits: minimumFractionDigits, maximumFractionDigits: maximumFractionDigits }).format(0);
    }
    return new Intl.NumberFormat(locale, { style: 'currency', currency, minimumFractionDigits: minimumFractionDigits , maximumFractionDigits: maximumFractionDigits }).format(number);
};

export const formatPercentage = (number, options = {}) => {
    const { locale = 'en-US', minimumFractionDigits = 0, maximumFractionDigits = 0 } = options;

    if (number === null || number === undefined) {
        return new Intl.NumberFormat(locale, { style: 'percent', minimumFractionDigits: minimumFractionDigits, maximumFractionDigits: maximumFractionDigits }).format(0);
    }
    return new Intl.NumberFormat(locale, { style: 'percent', minimumFractionDigits: minimumFractionDigits, maximumFractionDigits: maximumFractionDigits }).format(number);
};